/**
 * EmailQueue — lightweight in-memory queue with priority,
 * retry policy, and status tracking.
 *
 * Statuses: queued → processing → sent | failed | retry | cancelled
 * Higher priority items are processed first. Failed items
 * are retried up to maxRetries with exponential backoff.
 */

import type { EmailMessage, EmailStatus, EmailPriority } from './types';
import { QueueFullError, RetryExceededError } from './errors';
import { LoggerFactory } from '@/lib/logging';
import { analyticsService } from '@/lib/AnalyticsService';

const PRIORITY_ORDER: Record<EmailPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export interface QueuedEmail {
  id: string;
  message: EmailMessage;
  status: EmailStatus;
  priority: EmailPriority;
  retryCount: number;
  maxRetries: number;
  queuedAt: string;
  lastAttemptAt?: string;
  error?: string;
}

export interface EmailQueueOptions {
  maxSize?: number;
  maxRetries?: number;
  baseRetryDelayMs?: number;
}

const DEFAULTS: Required<EmailQueueOptions> = {
  maxSize: 200,
  maxRetries: 3,
  baseRetryDelayMs: 2000,
};

class EmailQueueImpl {
  private queue: QueuedEmail[] = [];
  private opts: Required<EmailQueueOptions>;
  private logger = LoggerFactory.create();

  constructor(opts: EmailQueueOptions = {}) {
    this.opts = { ...DEFAULTS, ...opts };
  }

  get size(): number {
    return this.queue.length;
  }

  get pending(): number {
    return this.queue.filter((e) => e.status === 'queued' || e.status === 'retry').length;
  }

  get failed(): number {
    return this.queue.filter((e) => e.status === 'failed').length;
  }

  get sentCount(): number {
    return this.queue.filter((e) => e.status === 'sent').length;
  }

  enqueue(message: EmailMessage): QueuedEmail {
    if (this.queue.length >= this.opts.maxSize) {
      throw new QueueFullError(this.opts.maxSize);
    }

    const item: QueuedEmail = {
      id: generateId(),
      message,
      status: 'queued',
      priority: message.metadata.priority,
      retryCount: 0,
      maxRetries: this.opts.maxRetries,
      queuedAt: new Date().toISOString(),
    };

    this.queue.push(item);
    this.sortQueue();
    this.logger.info('[email:queue] Enqueued', {
      emailId: item.id,
      to: message.to,
      template: message.metadata.template,
      priority: item.priority,
      queueSize: this.queue.length,
    });
    analyticsService.track('email_queued' as never, {
      emailId: item.id,
      template: message.metadata.template,
      priority: item.priority,
    } as never);
    return item;
  }

  dequeue(): QueuedEmail | null {
    const item = this.queue.find((e) => e.status === 'queued' || e.status === 'retry');
    if (!item) return null;
    item.status = 'processing';
    item.lastAttemptAt = new Date().toISOString();
    return item;
  }

  markSent(id: string, messageId?: string): void {
    const item = this.queue.find((e) => e.id === id);
    if (!item) return;
    item.status = 'sent';
    item.message.metadata.retryCount = item.retryCount;
    this.logger.info('[email:queue] Sent', {
      emailId: id,
      messageId,
      retryCount: item.retryCount,
    });
    analyticsService.track('email_sent' as never, {
      emailId: id,
      template: item.message.metadata.template,
      retryCount: item.retryCount,
    } as never);
    this.pruneCompleted();
  }

  markFailed(id: string, error: string): void {
    const item = this.queue.find((e) => e.id === id);
    if (!item) return;
    item.error = error;
    item.retryCount += 1;

    if (item.retryCount >= item.maxRetries) {
      item.status = 'failed';
      this.logger.error('[email:queue] Failed permanently', {
        emailId: id,
        error,
        retryCount: item.retryCount,
      });
      analyticsService.track('email_failed' as never, {
        emailId: id,
        template: item.message.metadata.template,
        error,
      } as never);
    } else {
      item.status = 'retry';
      this.logger.warn('[email:queue] Failed, will retry', {
        emailId: id,
        error,
        retryCount: item.retryCount,
        maxRetries: item.maxRetries,
      });
      analyticsService.track('email_retry' as never, {
        emailId: id,
        retryCount: item.retryCount,
      } as never);
    }
    this.sortQueue();
  }

  cancel(id: string): void {
    const item = this.queue.find((e) => e.id === id);
    if (!item) return;
    item.status = 'cancelled';
    this.logger.info('[email:queue] Cancelled', { emailId: id });
  }

  getRetryDelay(retryCount: number): number {
    return this.opts.baseRetryDelayMs * Math.pow(2, retryCount - 1);
  }

  getQueueStats(): {
    total: number;
    queued: number;
    processing: number;
    sent: number;
    failed: number;
    retry: number;
    cancelled: number;
  } {
    const stats = { total: 0, queued: 0, processing: 0, sent: 0, failed: 0, retry: 0, cancelled: 0 };
    for (const item of this.queue) {
      stats.total++;
      stats[item.status]++;
    }
    return stats;
  }

  getFailed(): QueuedEmail[] {
    return this.queue.filter((e) => e.status === 'failed');
  }

  clear(): void {
    this.queue = [];
  }

  private sortQueue(): void {
    this.queue.sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority];
      const pb = PRIORITY_ORDER[b.priority];
      if (pa !== pb) return pa - pb;
      return a.queuedAt.localeCompare(b.queuedAt);
    });
  }

  private pruneCompleted(): void {
    const sentCount = this.queue.filter((e) => e.status === 'sent').length;
    if (sentCount > 50) {
      const toRemove = sentCount - 50;
      let removed = 0;
      this.queue = this.queue.filter((e) => {
        if (e.status === 'sent' && removed < toRemove) {
          removed++;
          return false;
        }
        return true;
      });
    }
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `email-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const emailQueue = new EmailQueueImpl();

export { RetryExceededError };
